import React, { useRef, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown'; // ⬅️ add this at the top

import { MessageCircle, X, Send, Bot } from 'lucide-react';
import { ChatbotContext } from './ChatbotContext';

const ChatbotWidget = () => {
  const {
    isOpen,
    setIsOpen,
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    isTyping,
    setIsTyping,
    isLoading,
    setIsLoading
  } = useContext(ChatbotContext);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessageToBackend = async (message) => {
  try {
    const response = await fetch('https://backend.virtualipus.com/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message })
    });

    if (!response.ok)
      throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    return data.response;
  } catch (error) {
    console.error('Error sending message to backend:', error);
    return "Sorry, I'm having trouble connecting. Please try again later.";
  }
};


  const handleSendMessage = async () => {
    if (inputMessage.trim() === '' || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setIsLoading(true);

    try {
      const botResponseText = await sendMessageToBackend(userMessage.text);
      setTimeout(() => {
        const botResponse = {
          id: Date.now() + 1,
          text: botResponseText,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
        setIsLoading(false);
      }, 800);
    } catch {
      setTimeout(() => {
        const errorResponse = {
          id: Date.now() + 1,
          text: "Technical difficulties. Try again later or contact support.",
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponse]);
        setIsTyping(false);
        setIsLoading(false);
      }, 800);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
      {isOpen && (
        <div style={{ width: '350px', height: '500px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)', display: 'flex', flexDirection: 'column', marginBottom: '10px', border: '1px solid #e1e5e9' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e1e5e9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#4f46e5', color: 'white', borderRadius: '12px 12px 0 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bot size={18} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>Chat with</div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Virtual IP 🚀</div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {messages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '80%', padding: '8px 12px', borderRadius: msg.sender === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px', backgroundColor: msg.sender === 'user' ? '#4f46e5' : '#f3f4f6', color: msg.sender === 'user' ? 'white' : '#374151' }}>
                  <ReactMarkdown
  children={msg.text}
  components={{
    p: ({ children }) => <p style={{ margin: 0, marginBottom: '4px' }}>{children}</p>,
    strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
    li: ({ children }) => <li style={{ marginLeft: '16px' }}>{children}</li>
  }}
/>

                  <span style={{ fontSize: '11px', opacity: 0.7, textAlign: 'right', display: 'block' }}>{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '8px 12px', borderRadius: '12px 12px 12px 4px', backgroundColor: '#f3f4f6', display: 'flex', gap: '4px' }}>
                  {[0, 0.2, 0.4].map((delay, i) => (
                    <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9ca3af', animation: `typing 1.4s infinite ease-in-out ${delay}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ padding: '16px', borderTop: '1px solid #e1e5e9', display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isLoading ? "Sending..." : "Type your message..."}
              disabled={isLoading}
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '20px', outline: 'none', fontSize: '14px', backgroundColor: isLoading ? '#f9fafb' : 'white' }}
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || inputMessage.trim() === ''}
              style={{ padding: '8px 12px', backgroundColor: (isLoading || inputMessage.trim() === '') ? '#d1d5db' : '#4f46e5', color: 'white', border: 'none', borderRadius: '20px', cursor: (isLoading || inputMessage.trim() === '') ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button onClick={() => setIsOpen(!isOpen)} style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#4f46e5', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)', position: 'relative', transition: 'all 0.2s ease' }}>
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        {!isOpen && (
          <>
            <span style={{ position: 'absolute', top: '8px', right: '8px', width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '50%', border: '2px solid white' }}></span>
            <span style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: '#ef4444', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold', minWidth: '16px', textAlign: 'center' }}>1</span>
          </>
        )}
      </button>

      <style>{`
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-10px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ChatbotWidget;